/**
 * Entrada no plano de controle.
 *
 * A senha nunca e guardada: vai no corpo da chamada e o que fica na sessao do navegador e o token
 * devolvido pelo backend.
 *
 * Aqui o login tem uma recusa a mais que a do viewer: alem de credencial invalida (401), o backend
 * nega quem NAO tem a permissao de administracao do catalogo (403). Sao coisas diferentes e a tela
 * precisa dizer qual das duas - "senha errada" para quem so nao e administrador manda a pessoa
 * tentar de novo para sempre.
 *
 * O desenho e o da entrada do Auth Manager: fundo em gradiente radial, cartao em duas colunas - o
 * produto a esquerda, o formulario a direita - e o tema alternavel ja na entrada.
 */

import { Button, Card, Field, Icon, IconButton, StatusBadge } from "@ledger-glass/react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError, setToken } from "../api/client";
import { Alert } from "../components/Alert";
import { aplicarTema, gravarTema, lerTema, type Tema } from "../shell/theme";
import { login } from "./api";

/**
 * O 403 tem duas causas muito diferentes, e tratar as duas com a mesma frase custou um incidente:
 * o perfil nao ter a permissao, ou a permissao existir e o Auth ter resolvido o resource ERRADO
 * (um do tenant, quando o app esperava o global). A primeira e com quem administra o Auth; a
 * segunda e configuracao nossa, e ninguem adivinha isso olhando "voce nao tem permissao".
 */
function motivoDa403(erro: ApiError): string {
  if (erro.detail.includes("resolveu_resource_do_tenant")) {
    return "O Auth respondeu que você tem a permissão, mas resolveu um resource do tenant onde este app espera um global. É configuração do catálogo de resources, não da sua conta.";
  }
  if (erro.detail.includes("escopo_indeterminado")) {
    return "O Auth respondeu sem dizer o escopo do resource, e sem isso o acesso não é liberado. Avise quem administra o Auth.";
  }
  return "Sua conta entrou, mas nao tem permissao de administracao do catalogo.";
}

export function LoginScreen({
  onEntered,
  notice,
}: {
  onEntered: () => void;
  notice?: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tema, setTema] = useState<Tema>(lerTema);

  useEffect(() => {
    aplicarTema(tema);
    gravarTema(tema);
  }, [tema]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const resultado = await login(email, password);
      setToken(resultado.access_token);
      onEntered();
    } catch (exc) {
      // 401 do Auth e credencial errada; o resto e servico indisponivel - dizer qual dos dois.
      setError(
        exc instanceof ApiError && exc.status === 401
          ? "E-mail ou senha incorretos."
          : exc instanceof ApiError && exc.status === 403
            ? motivoDa403(exc)
            : exc instanceof Error
              ? exc.message
              : "Falha ao entrar.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-page">
      <header className="auth-utility-bar">
        <IconButton
          aria-pressed={tema === "claro"}
          icon={tema === "escuro" ? "sun" : "moon"}
          label={tema === "escuro" ? "Usar tema claro" : "Usar tema escuro"}
          onClick={() => setTema((atual) => (atual === "escuro" ? "claro" : "escuro"))}
        />
      </header>

      <main className="auth-main" id="entrar" tabIndex={-1}>
        <Card className="auth-card" tone="blue">
          <div className="auth-heading">
            <p className="eyebrow">Ecossistema Vetor 4</p>
            <div className="auth-title-lockup">
              <span className="auth-title-icon">
                <Icon name="dashboard" size={96} />
              </span>
              <h1>Report Manager</h1>
            </div>
            <p>Catálogo de relatórios, conexões com o Power BI e as regras de quem vê o quê.</p>
            <StatusBadge tone="tech">Administração</StatusBadge>
          </div>

          <div className="auth-workflow">
            {notice === undefined ? null : <Alert tone="warning">{notice}</Alert>}
            <form className="auth-form" noValidate onSubmit={submit}>
              <Field id="login-email" label="E-mail" required>
                {(props) => (
                  <input
                    {...props}
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>

              <Field
                id="login-password"
                label="Senha"
                required
                error={error ?? undefined}
              >
                {(props) => (
                  <input
                    {...props}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                )}
              </Field>

              <Button
                type="submit"
                variant="primary"
                loading={pending}
                loadingLabel="Entrando…"
                disabled={email.trim() === "" || password === ""}
              >
                Entrar
              </Button>
            </form>
            <p className="auth-support">
              Acesso pela autenticação do ecossistema, com permissão de administração do catálogo.
            </p>
          </div>
        </Card>
      </main>
    </div>
  );
}
